<script>
import moment from "moment/moment"
import { mapActions, mapGetters } from "vuex"
import CookieBanner from "@/components/CookieBanner.vue"
import ThemedLoading from "@/components/ThemedLoading.vue"
import ConfirmationProcessing from "@/components/confirmation/ConfirmationProcessing.vue"
import Cookies from "js-cookie"
import { setTheme } from "@/utils/set-theme"
import { isReduxiDomain } from "@/theme-helper"
import ToastMessage from "./components/shared/ToastMessage.vue"
import { fetchTranslations } from "./utils/i18n-utils"
import { usePullToRefresh } from "@/composables/usePullToRefresh"
import MarketingConsentBanner from "@/components/MarketingConsentBanner.vue"
import ThemeCustomizerPanel from "@/views/ThemeCustomizerView.vue"

export default {
    name: "App",
    components: {
        MarketingConsentBanner,
        ConfirmationProcessing,
        ThemedLoading,
        CookieBanner,
        ToastMessage,
        ThemeCustomizerPanel,
    },
    setup() {
        const { init, destroy } = usePullToRefresh()
        return { initPullToRefresh: init, destroyPullToRefresh: destroy }
    },
    data() {
        return {
            isLoading: true,
            section: "Head",
            confirmation: null,
            ptrInstance: null,
        }
    },
    watch: {
        "$i18n.locale": function (locale) {
            moment.locale(locale)

            fetchTranslations(locale).then((translations) => {
                this.$i18n.mergeLocaleMessage(locale, translations)
            })
        },
        "$cookieConsent.hasConsented": {
            immediate: true,
            handler: function (hasConsented) {
                if (hasConsented) {
                    this.tryLoadingAnalytics()
                }
            },
        },
        $route: {
            immediate: true,
            handler: function () {
                this.processAnyConfirmations()
            },
        },
    },
    methods: {
        ...mapActions("reduxi", {
            setSiteInfo: "setSiteInfo",
        }),
        ...mapActions("auth", {
            setPermissions: "setPermissions",
            checkIfUserSetUp: "checkIfUserSetUp",
        }),
        ...mapActions("appLayoutModule", {
            startListening: "startListening",
            stopListening: "stopListening",
        }),
        tryLoadingAnalytics() {
            if (process.env.NODE_ENV !== "production") {
                console.log("not loading analytics, must be in production")

                return
            }

            if (this.$cookieConsent.hasConsentTo("analyticsCookies")) {
                let smartLookScript = document.createElement("script")
                smartLookScript.type = "text/javascript"
                smartLookScript.text =
                    "window.smartlook||(function(d) { var o=smartlook=function(){ o.api.push(arguments)},h=d.getElementsByTagName('head')[0]; var c=d.createElement('script');o.api=new Array();c.async=true;c.type='text/javascript'; c.charset='utf-8';c.src='https://web-sdk.smartlook.com/recorder.js';h.appendChild(c); })(document); smartlook('init', 'ea4b627aca4aed35c313239e4ca7965b8ef292f8', { region: 'eu' });"
                document.head.append(smartLookScript)

                let gTagManagerScript = document.createElement("script")
                gTagManagerScript.async = true
                gTagManagerScript.src = "https://www.googletagmanager.com/gtag/js?id=G-PSYPS66RDG"
                document.head.append(gTagManagerScript)

                let gAnalyticsScript = document.createElement("script")
                gAnalyticsScript.type = "text/javascript"
                gAnalyticsScript.text =
                    "window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', 'G-PSYPS66RDG');"
                document.head.append(gAnalyticsScript)
            }
        },
        setMainTheme(theme) {
            setTheme(document.documentElement, theme)
        },
        processAnyConfirmations() {
            this.$router.onReady(() => {
                const queryParams = this.$route.query
                const { confirmationAction, confirmationId } = queryParams

                if (
                    confirmationAction == null ||
                    confirmationId == null ||
                    Number.isNaN(confirmationId)
                ) {
                    this.confirmation = null

                    return
                }

                this.confirmation = {
                    id: Number.parseInt(confirmationId),
                    action: confirmationAction,
                }
            })
        },
        setupUserAndPermissions() {
            this.$auth.load().then(() => {
                let user = this.$auth.user()

                if (!user) {
                    this.isLoading = false

                    return
                }

                if (user.locale) {
                    Cookies.set("locale", user.locale)
                    this.$i18n.locale = user.locale
                }

                this.setPermissions(user.permissions)

                this.checkIfUserSetUp()
                    .then((isSetUp) => {
                        if (
                            !isSetUp &&
                            this.$route.path !== "/setup" &&
                            this.$route.path !== "/auth/token-validation"
                        ) {
                            this.$router.push("/setup")
                        }
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            })
        },
        initIOSPullToRefresh() {
            this.initPullToRefresh({
                onRefresh: () => {
                    return new Promise((resolve) => {
                        setTimeout(() => {
                            window.location.reload()
                            resolve()
                        }, 1000)
                    })
                },
            })
        },
    },
    computed: {
        ...mapGetters("reduxi", {
            isCookieBannerVisible: "isCookieBannerVisible",
            siteInfo: "getSiteInfo",
        }),
        ...mapGetters("siteInfo", {
            maintenanceStatus: "maintenanceStatus",
        }),
        isReduxiDomain() {
            return isReduxiDomain(this.siteInfo?.host)
        },
    },
    mounted() {
        this.startListening()
        this.setupUserAndPermissions()
        this.initIOSPullToRefresh()
    },
    beforeDestroy() {
        this.stopListening()
        this.destroyPullToRefresh()
    },
}
</script>

<template>
    <div id="app">
        <ThemedLoading
            :active="isLoading"
            :is-full-page="true"
            loader="bars"
            :opacity="1"
            :can-cancel="false"
        >
        </ThemedLoading>
        <ConfirmationProcessing
            v-if="confirmation"
            :action="confirmation.action"
            :id="confirmation.id"
        />

        <router-view v-if="!isLoading"></router-view>
        <CookieBanner />
        <MarketingConsentBanner />
        <portal-target name="body" />

        <ToastMessage />
        <ThemeCustomizerPanel />

        <button
            v-if="$auth.check() && isReduxiDomain && $auth.user()?.isDemoUser"
            @click="$store.dispatch('themeCustomizer/toggleThemeCustomizer')"
            class="theme-toggle-btn"
            title="Open Theme Customizer"
        >
            🎨
        </button>
    </div>
</template>

<style>
.theme-toggle-btn {
    position: fixed;
    bottom: 20px;
    right: 20px;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #0db4ac;
    color: white;
    border: none;
    font-size: 24px;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    z-index: 9999;
    transition: all 0.3s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.theme-toggle-btn:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
}

.theme-toggle-btn:active {
    transform: scale(0.95);
}
</style>
